import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { SectionCard, errorStyle } from "./common.js";

/**
 * The owner's switch for checking a customer's date of birth, on the phone and on the online booking page (migration 0068).
 * Off by default. Only the owner (or the platform admin) can change it; managers and staff see the setting.
 */
export function DobSection() {
  const { activeBusinessId, memberships, isPlatformAdmin } = useAuth();
  const isOwner = isPlatformAdmin || memberships.some((m) => m.business_id === activeBusinessId && m.role === "owner");
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeBusinessId) return;
    void supabase
      .from("businesses")
      .select("require_dob")
      .eq("id", activeBusinessId)
      .single()
      .then(({ data }: { data: { require_dob: boolean } | null }) => setOn(data ? data.require_dob === true : null));
  }, [activeBusinessId]);

  if (on === null) return null;

  async function change(next: boolean) {
    if (!activeBusinessId) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("rpc_portal_set_dob_verification", { p_business_id: activeBusinessId, p_on: next });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setOn(next);
  }

  return (
    <SectionCard
      title="Date of birth check"
      description="Ask customers to confirm their date of birth before their bookings are shown or changed, on the phone and on the online booking page. Customers with no date of birth on file give it with their next booking and it is saved. Three wrong tries stop the call or the online session."
    >
      <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: isOwner && !busy ? "pointer" : "default" }}>
        <input type="checkbox" checked={on} disabled={!isOwner || busy} onChange={(e) => void change(e.target.checked)} style={{ marginTop: 3 }} />
        <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--rc-text)" }}>Check date of birth</span>
          <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>
            {isOwner ? "Takes effect within a minute." : "Only the business owner can change this."} You can add or correct a customer's date of birth on the Customers page.
          </span>
        </span>
      </label>
      {error && <p style={errorStyle}>{error}</p>}
    </SectionCard>
  );
}
